import * as FileSaver from "file-saver";
import ExcelJS from "exceljs";

const prettify = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
const HEADER_FONT = { name: "Calibri", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
const BODY_FONT = { name: "Calibri", size: 11 };
const THIN_BORDER = {
  top: { style: "thin", color: { argb: "FFBFBFBF" } },
  left: { style: "thin", color: { argb: "FFBFBFBF" } },
  bottom: { style: "thin", color: { argb: "FFBFBFBF" } },
  right: { style: "thin", color: { argb: "FFBFBFBF" } },
};
const ZEBRA_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F7FB" } };

const looksNumeric = (v) => {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "number") return Number.isFinite(v);
  const n = Number(v);
  return !Number.isNaN(n) && String(v).trim() !== "";
};

const coerceCell = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isFinite(v) ? v : "";
  if (typeof v === "boolean") return v;
  const s = String(v);
  if (looksNumeric(s) && !/^0\d+/.test(s.trim())) {
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return s;
};

const selectKeys = (rows, options) => {
  const { includedFields, excludedFields = [] } = options || {};
  if (includedFields && includedFields.length > 0) {
    return includedFields.filter((k) => !excludedFields.includes(k));
  }
  if (rows && rows.length > 0) {
    return Object.keys(rows[0]).filter(
      (k) => !excludedFields.includes(k) && !k.startsWith("_"),
    );
  }
  return [];
};

const writeStyledSheet = (ws, rows, keys, titleDictionary = {}, startRow = 1) => {
  if (keys.length === 0) return;

  const headers = keys.map((k) => titleDictionary[k] || prettify(k));

  const headerRow = ws.getRow(startRow);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = THIN_BORDER;
  });
  headerRow.height = 22;

  const firstNonEmpty = {};
  keys.forEach((k) => {
    for (const r of rows) {
      if (r[k] !== undefined && r[k] !== null && r[k] !== "") {
        firstNonEmpty[k] = r[k];
        break;
      }
    }
  });

  rows.forEach((row, rIdx) => {
    const excelRow = ws.getRow(startRow + 1 + rIdx);
    keys.forEach((k, cIdx) => {
      const cell = excelRow.getCell(cIdx + 1);
      cell.value = coerceCell(row[k]);
      cell.font = BODY_FONT;
      cell.border = THIN_BORDER;
      const isNum = typeof cell.value === "number";
      cell.alignment = {
        vertical: "middle",
        horizontal: isNum ? "right" : "left",
        wrapText: false,
      };
      if (isNum && !Number.isInteger(cell.value)) {
        cell.numFmt = "#,##0.00";
      } else if (isNum) {
        cell.numFmt = "#,##0";
      }
      if (rIdx % 2 === 1) {
        cell.fill = ZEBRA_FILL;
      }
    });
  });

  keys.forEach((k, i) => {
    const header = headers[i];
    const sampleVal = firstNonEmpty[k];
    const sampleLen = sampleVal !== undefined ? String(sampleVal).length : 0;
    const maxDataLen = rows.reduce((acc, r) => {
      const s = r[k] === undefined || r[k] === null ? "" : String(r[k]);
      return Math.max(acc, s.length);
    }, 0);
    const width = Math.min(
      50,
      Math.max(12, header.length + 2, Math.max(sampleLen, maxDataLen) + 2),
    );
    ws.getColumn(i + 1).width = width;
  });

  ws.views = [{ state: "frozen", ySplit: startRow }];
};

const exportToExcel = async (rows, fileName, options = {}) => {
  const {
    includedFields,
    titleDictionary = {},
    excludedFields = [],
    chartImage,
    chartImages,
    chartTitle,
    sheetName = "Sheet1",
  } = options;

  const images = chartImages && chartImages.length > 0
    ? chartImages
    : chartImage
      ? [chartImage]
      : [];

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Mewahtrans GPS System";
  workbook.created = new Date();

  const keys = selectKeys(rows, { includedFields, excludedFields });
  const hasChart = images.length > 0;

  const dataSheetName = hasChart ? "Data" : sheetName;
  const dataWs = workbook.addWorksheet(dataSheetName, {
    views: [{ showGridLines: false }],
  });

  let dataStartRow = 1;

  if (hasChart) {
    const colSpan = Math.max(6, keys.length);

    if (chartTitle) {
      const titleCell = dataWs.getCell("A1");
      titleCell.value = chartTitle;
      titleCell.font = { name: "Calibri", size: 14, bold: true, color: { argb: "FF1F4E78" } };
      titleCell.alignment = { vertical: "middle" };
      dataWs.getRow(1).height = 24;
      dataWs.mergeCells(1, 1, 1, colSpan);
      dataStartRow = 2;
    }

    const hintCell = dataWs.getCell(dataStartRow, 1);
    hintCell.value = "Tip: select the table below, then Insert → Chart to build an editable chart. The \"Chart\" tab shows a preview of how it looks in the app.";
    hintCell.font = { name: "Calibri", size: 10, italic: true, color: { argb: "FF555555" } };
    hintCell.alignment = { vertical: "middle", wrapText: true };
    hintCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF9E6" } };
    dataWs.getRow(dataStartRow).height = 30;
    dataWs.mergeCells(dataStartRow, 1, dataStartRow, colSpan);
    dataStartRow += 2;
  }

  if (keys.length > 0 && rows && rows.length > 0) {
    writeStyledSheet(dataWs, rows, keys, titleDictionary, dataStartRow);
  }

  if (hasChart) {
    try {
      const chartWs = workbook.addWorksheet("Chart", {
        views: [{ showGridLines: false }],
      });

      let row = 1;
      if (chartTitle) {
        const titleCell = chartWs.getCell("A1");
        titleCell.value = chartTitle;
        titleCell.font = { name: "Calibri", size: 14, bold: true, color: { argb: "FF1F4E78" } };
        titleCell.alignment = { vertical: "middle" };
        chartWs.getRow(1).height = 24;
        chartWs.mergeCells(1, 1, 1, 10);
        row = 2;
      }

      images.forEach((imgSrc) => {
        const base64 = imgSrc.startsWith("data:") ? imgSrc.split(",")[1] : imgSrc;
        const imageId = workbook.addImage({ base64, extension: "png" });
        chartWs.addImage(imageId, {
          tl: { col: 0, row },
          ext: { width: 820, height: 380 },
          editAs: "oneCell",
        });
        row += 22;
      });
    } catch (err) {
      console.warn("Failed to embed chart image:", err);
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  FileSaver.saveAs(blob, fileName + ".xlsx");
};

export default exportToExcel;
